/**
 * Um único componente de nó atende os 7 tipos de bloco.
 *
 * O que muda entre tipos é o ícone, o resumo e — o que importa de verdade — **as portas**, que vêm
 * de `sourceHandles()`. O nó de botões renderiza uma porta por opção com o `option.id` como id do
 * handle; a condição renderiza `true`/`false`; blocos que executam algo ganham também `erro`. São
 * exatamente os ids que o interpretador procura, então a aresta desenhada aqui é a que o fluxo segue.
 *
 * Sobre a leitura das portas: antes a entrada era um ponto de 11px solto na lateral esquerda e as
 * saídas eram pontos na borda, sem indicar que ali se conecta algo. Agora entrada e saídas são
 * **linhas rotuladas** com o conector ancorado nelas, e a cor do conector é a mesma do traço da
 * aresta que sai dele (ver `TONE_COLORS` em `flow/mapping.ts`).
 *
 * A cor do chip do ícone é a da **família** do bloco, não a do tipo: três cores em vez de sete. O
 * porquê está em `BLOCK_FAMILIES`.
 *
 * **A exceção de tamanho do painel mora aqui.** A escala diz nada abaixo de 12px (ver `PROSA` em
 * `ui.tsx`), e este arquivo tem três `text-[10px]`: "entrada", "INÍCIO" e "PAGE". São palavras de até
 * seis letras em caixa alta, dentro de um nó de 256px que precisa caber dezenas de vezes na tela —
 * selo, não frase. Todo o resto do nó segue a escala, e há teste garantindo que a exceção não
 * escorregue para outros arquivos.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import type {
  BackendWebhookBlockData,
  BrowserCodeBlockData,
  BrowserWebhookBlockData,
  ButtonsBlockData,
  ConditionBlockData,
  InputBlockData,
  MessageBlockData,
} from '@merlin/flow-engine';
import { BLOCK_META, blockChip, sourceHandles, TONE_COLORS, type AnyBlockNode } from '../flow/mapping.js';
import { BLOCK_ICONS } from './icons.js';

function summarize(type: string, block: unknown): string {
  switch (type) {
    case 'message':
      return (block as MessageBlockData).text || 'sem texto';
    case 'input': {
      const data = block as InputBlockData;
      return `${data.prompt || 'sem pergunta'} → {{${data.saveAs}}}`;
    }
    case 'buttons':
      return (block as ButtonsBlockData).prompt || 'sem pergunta';
    case 'condition': {
      const data = block as ConditionBlockData;
      if (data.mode === 'expression') return data.expression || 'sem expressão';
      const rules = data.rules ?? [];
      return rules.length ? `${rules.length} regra(s), combinadas com ${data.combinator ?? 'and'}` : 'sem regras';
    }
    case 'browserCode': {
      const data = block as BrowserCodeBlockData;
      return `modo ${data.executionMode}${data.saveAs ? ` → {{${data.saveAs}}}` : ''}`;
    }
    case 'browserWebhook': {
      const data = block as BrowserWebhookBlockData;
      return `${data.method} ${data.url || 'sem URL'}`;
    }
    case 'backendWebhook': {
      const data = block as BackendWebhookBlockData;
      const secrets = data.secretRefs?.length ? ` · ${data.secretRefs.length} segredo(s)` : '';
      return `${data.method ?? 'POST'} ${data.url || 'sem URL'}${secrets}`;
    }
    default:
      return '';
  }
}

/**
 * Cor e preenchimento do conector. Tamanho, posição e reação ao mouse ficam no CSS (`index.css`).
 *
 * **Vazado** quando nada está ligado ali, **preenchido** quando está. É o que faz a saída livre se
 * destacar: um círculo oco lê como tomada esperando fio, e é justamente onde a pessoa precisa puxar.
 */
const handleStyle = (color: string, connected: boolean): React.CSSProperties => ({
  background: connected ? color : '#fff',
  borderColor: color,
});

export function BlockNode({ type, data, selected }: NodeProps<AnyBlockNode>) {
  const meta = BLOCK_META[type as keyof typeof BLOCK_META];
  const Icone = BLOCK_ICONS[type as keyof typeof BLOCK_ICONS];
  const block = data.block;
  const handles = sourceHandles(type as never, block);
  const label = (block as { label?: string }).label ?? meta.label;
  const isStart = Boolean(data.isStart);
  const unreachable = Boolean(data.unreachable);
  const connected = data.connections?.connectedSourceHandles ?? [];
  const hasIncoming = Boolean(data.connections?.hasIncoming);

  // Modo `page` é o único ajuste que muda a superfície de segurança do bloco, então aparece já
  // no canvas — não só escondido no formulário.
  const pageMode =
    type === 'browserCode' && (block as BrowserCodeBlockData).executionMode === 'page';

  return (
    /*
     * Duas coisas amarradas nesta caixa.
     *
     * **Sem `overflow-hidden`, de propósito.** Os conectores ficam metade para fora, e recortá-los era
     * o que os deixava invisíveis — o defeito que dava a impressão de que a saída não tinha de onde
     * puxar. Em troca, cada filho de ponta arredonda o próprio canto, já que a caixa não recorta.
     *
     * **Contraste com o canvas.** Antes era borda `slate-200` e `shadow-sm` sobre fundo `slate-50`:
     * branco sobre quase-branco, com a borda quase invisível, e num canvas cheio o olho não separava
     * um card do outro. Agora a borda é `slate-300`, a sombra tem duas camadas e o fundo escureceu um
     * degrau (ver `CanvasArea`) — a diferença vem dos dois lados.
     */
    <div
      className={`w-64 rounded-xl border bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-2px_rgba(15,23,42,0.10)] transition ${
        selected ? 'border-slate-900 ring-2 ring-slate-900/20' : 'border-slate-300'
      } ${unreachable ? 'opacity-70' : ''}`}
      style={unreachable ? { borderStyle: 'dashed' } : undefined}
    >
      {/*
        Porta de entrada como linha rotulada. O handle não recebe id de propósito: as arestas já
        gravadas não têm `targetHandle`, e nomear a porta agora quebraria o casamento delas.

        `rounded-t-[11px]` porque esta faixa virou o primeiro filho quando a barra de cor do tipo saiu
        (quem carrega a identidade agora é o ícone, logo abaixo). Sem o arredondamento, o fundo cinza
        dela pinta por cima dos cantos do card e o topo reaparece quadrado. 11px e não 12px: é o raio
        externo menos a espessura da borda.
      */}
      <div
        className="relative flex items-center gap-2 rounded-t-[11px] border-b border-slate-200 bg-slate-50 px-3 py-2"
        title="Entrada: arraste daqui para escolher de qual bloco este vem, ou solte uma linha aqui."
      >
        <Handle
          type="target"
          position={Position.Left}
          // Explícito, ainda que seja o padrão: é o que permite puxar a partir de um bloco solto,
          // em vez de obrigar a começar sempre pela saída do bloco anterior.
          isConnectableStart
          style={handleStyle('#94a3b8', hasIncoming)}
        />
        {/* `slate-500`, não `slate-400`: sobre `slate-50` o 400 dá 2,8:1 e some para muita gente. */}
        <span className="text-[10px] font-semibold tracking-wide text-slate-500 uppercase">entrada</span>
      </div>

      {/*
        Ícone + nome do bloco na mesma linha, e o tipo migrou para a linha de resumo.
        Antes eram três níveis empilhados — selo do tipo, nome, resumo — e o selo repetia em texto o
        que a cor já dizia. Com o ícone carregando tipo e família, sobra uma linha a menos e o nome do
        bloco, que é o que a pessoa procura ao varrer o canvas, fica no topo.
      */}
      <div className="flex items-start gap-3 px-3 py-3">
        {/* A caixa do ícone é centrada na primeira linha do título (20px), sem margem de olho. */}
        <span className="flex h-5 shrink-0 items-center">
          <span className={`grid size-7 place-items-center rounded-lg ${blockChip(type as never)}`} title={meta.label}>
            <Icone size={15} />
          </span>
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{label}</p>
            {isStart ? (
              <span className="rounded bg-slate-900 px-2 py-1 text-[10px] leading-none font-bold text-white">
                INÍCIO
              </span>
            ) : null}
            {pageMode ? (
              <span
                className="rounded bg-amber-100 px-2 py-1 text-[10px] leading-none font-bold text-amber-900"
                title="Este código roda no contexto da página, com acesso total ao site do cliente."
              >
                PAGE
              </span>
            ) : null}
          </div>

          <p className="line-clamp-2 text-xs leading-snug text-slate-500">
            <span className="font-medium text-slate-600">{meta.label}</span> · {summarize(type, block)}
          </p>

          {unreachable ? (
            <p
              className="text-xs font-medium text-amber-700"
              title="Não existe caminho do bloco inicial até aqui, então este bloco nunca vai rodar."
            >
              não alcançável
            </p>
          ) : null}
        </div>
      </div>

      {/* Uma linha por porta de saída, com o conector ancorado nela. */}
      <div className="rounded-b-[11px] border-t border-slate-200 bg-slate-50">
        {handles.map((handle, index) => {
          const isConnected = connected.includes(handle.id);
          const color = TONE_COLORS[handle.tone];
          return (
            <div
              key={handle.id}
              className={`relative flex items-center justify-end gap-2 px-3 py-2 text-xs ${
                index === handles.length - 1 ? 'rounded-b-[11px]' : ''
              }`}
              style={{ minHeight: 28, color }}
              title={
                isConnected
                  ? `Saída "${handle.label}" — já conectada. Arraste o ponto para ligar em outro bloco.`
                  : `Saída "${handle.label}" — arraste este ponto até a entrada de outro bloco.`
              }
            >
              {/* Seta discreta apontando o conector: diz "o fio sai por aqui". */}
              <span className="truncate font-medium">{handle.label}</span>
              <span aria-hidden="true" className="text-xs leading-none opacity-70">
                →
              </span>
              {/* Sem `top`: o container é `relative`, e o próprio React Flow centra o handle nele. */}
              <Handle
                id={handle.id}
                type="source"
                position={Position.Right}
                style={handleStyle(color, isConnected)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
